/**
 * RoomTellMore (PPL-29 / redesigned PPL-62) — the "tell me more" explainer.
 *
 * READ-ONLY, NO-COMMITMENT explainer. The only exit is "back to the
 * invitation". There is NO accept path from this screen and NO decline
 * path either — the choice belongs on the invitation screen (PPL-26),
 * not buried inside the explainer.
 *
 * Surfaces this page is mounted at:
 *   1. `/rooms/:mediationId/tell-me-more` — authenticated.
 *   2. `/i/:token/tell-me-more` — UNAUTHENTICATED public sub-route of the
 *      `RoomInviteByToken` shell. The component reads which one it is from
 *      the params (`mediationId` vs `token`) and renders identically either
 *      way; only the "back" target differs.
 *
 * Shared content invariant: the four-stage explainer + "things you don't
 * have to do" list + the asymmetry note are IDENTICAL to the app version
 * at `app/src/screens/Room/RoomTellMoreScreen.js`. Keep them in
 * sync field-for-field; if one side changes a stage description, change
 * the other.
 *
 * Voice: lowercase throughout, plain prose, no therapy jargon.
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ─── Shared content (IDENTICAL to RoomTellMoreScreen.js on app) ─────────

interface Stage {
  n: number;
  title: string;
  body: React.ReactNode;
}

const STAGES: Stage[] = [
  {
    n: 1,
    title: 'solo prep',
    body: (
      <>
        before anything is shared, each of you has a separate, private
        conversation with f8. f8 is calibrated to each side —{' '}
        <span style={{ color: '#f5d0fe', fontWeight: 600 }}>
          the questions you get are not the questions they get
        </span>
        . your prep is yours alone. nothing crosses over.
      </>
    ),
  },
  {
    n: 2,
    title: 'sharing rules',
    body: (
      <>
        both of you set granular consent toggles for what can be referenced in
        the joint room — things from your prep, from past patterns, from shared
        history.{' '}
        <span style={{ color: '#f5d0fe', fontWeight: 600 }}>
          every toggle defaults off.
        </span>{' '}
        nothing crosses without consent.
      </>
    ),
  },
  {
    n: 3,
    title: 'the open room',
    body: (
      <>
        f8 opens the room by playing back, with permission from both sides, an
        even-handed synthesis of what each of you said in prep. only what you
        each opted in to is surfaced.{' '}
        <span style={{ color: '#f5d0fe', fontWeight: 600 }}>
          you both see the same thing at the same time.
        </span>
      </>
    ),
  },
  {
    n: 4,
    title: 'ongoing work',
    body: (
      <>
        the shared room stays open for as long as it is useful. either of you
        can step out, pause, or end it. periodic check-ins from f8 ask whether
        it is still working — and route a different way if it is not.
      </>
    ),
  },
];

const FREEDOMS: string[] = [
  'you do not have to accept right now.',
  'you do not have to share anything you do not want to.',
  'you do not have to keep going if it stops feeling useful.',
  'you do not have to answer every prep question.',
  'you can pause or end at any point — solo or jointly.',
];

function buildAsymmetryNote(inviterHandle: string | null | undefined): React.ReactNode {
  const handle = (inviterHandle || '').trim();
  if (!handle) {
    return (
      <>
        the person who invited you has had previous talks with f8. if anything
        from those is referenced in your shared room, you&apos;ll be told —
        never slipped in unannounced.
      </>
    );
  }
  return (
    <>
      <span style={{ color: '#fff', fontWeight: 600 }}>{handle}</span> has had
      previous talks with f8. if anything from those is referenced in your
      shared room, you&apos;ll be told — never slipped in unannounced.
    </>
  );
}

// ─── Inline keyframes (injected once) ────────────────────────────────────

const KEYFRAMES_ID = 'rooms-tellmore-keyframes';
function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes rooms-tellmore-rise {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes rooms-tellmore-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(217, 70, 239, 0.45); }
      70%  { box-shadow: 0 0 0 9px rgba(217, 70, 239, 0); }
      100% { box-shadow: 0 0 0 0 rgba(217, 70, 239, 0); }
    }
  `;
  document.head.appendChild(style);
}

const reduceMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function revealStyle(delaySeconds: number): React.CSSProperties {
  if (reduceMotion) return {};
  return {
    opacity: 0,
    transform: 'translateY(10px)',
    animation: `rooms-tellmore-rise 0.6s cubic-bezier(0.22, 0.9, 0.3, 1) ${delaySeconds}s forwards`,
  };
}

// ─── Component ────────────────────────────────────────────────────────────

interface LocationStateProps {
  inviterName?: string | null;
  inviterHandle?: string | null;
}

const RoomTellMore: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ mediationId?: string; token?: string }>();

  useEffect(() => {
    ensureKeyframes();
  }, []);

  const state = (window.history.state?.usr || {}) as LocationStateProps;
  const inviterHandle = state.inviterHandle || state.inviterName || null;
  const asymmetryNote = buildAsymmetryNote(inviterHandle);

  // "back to the invitation" target. Prefer browser back when there's a
  // history entry to pop — this preserves the previous /i/:token state
  // (scripted projection + inline preview) instead of remounting the page
  // fresh. Fall back to direct navigation only when there's no history (e.g.
  // the invitee deep-linked to the explainer from an email or shared URL).
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    if (params.token) {
      navigate(`/i/${params.token}`);
      return;
    }
    if (params.mediationId) {
      navigate(`/rooms/${params.mediationId}/invite`);
      return;
    }
    navigate('/rooms');
  };

  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        minHeight: '100vh',
        color: '#fff',
        fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
        WebkitFontSmoothing: 'antialiased',
        background:
          'radial-gradient(1400px 760px at 14% 2%, rgba(124, 58, 237, 0.16), transparent 58%),' +
          ' radial-gradient(1200px 760px at 90% 100%, rgba(217, 70, 239, 0.11), transparent 60%),' +
          ' #0a0510',
      }}
    >
      {/* ── top bar ─────────────────────────────────────── */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          padding: '16px 22px',
          background: 'rgba(10, 5, 16, 0.72)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label="back to the invitation"
          style={{
            justifySelf: 'start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 13px 8px 10px',
            borderRadius: 999,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: 'rgba(232, 220, 250, 0.72)',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.15s ease, color 0.15s ease',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M14.5 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          back
        </button>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: '#fff',
            whiteSpace: 'nowrap',
          }}
        >
          tell me more
        </span>
        <span />
      </header>

      {/* ── content ─────────────────────────────────────── */}
      <main
        id="main-content"
        style={{
          maxWidth: 680,
          margin: '0 auto',
          padding: '52px 24px 96px',
        }}
      >
        {/* hero */}
        <section style={revealStyle(0.02)}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#f0abfc',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#d946ef',
                animation: reduceMotion
                  ? 'none'
                  : 'rooms-tellmore-pulse 2.6s ease-out infinite',
              }}
            />
            f8 · before you decide
          </span>
          <h1
            style={{
              margin: '18px 0 0',
              fontSize: 34,
              lineHeight: 1.12,
              fontWeight: 800,
              letterSpacing: '-0.03em',
            }}
          >
            here&apos;s what a room actually is.
          </h1>
          <p
            style={{
              margin: '16px 0 0',
              fontSize: 18,
              lineHeight: 1.55,
              color: 'rgba(245, 243, 255, 0.86)',
              maxWidth: 600,
            }}
          >
            here is what happens if you say yes — and what stays true at every
            step, whether you do or not.
          </p>
        </section>

        {/* stages timeline */}
        <section style={{ marginTop: 44, ...revealStyle(0.1) }}>
          {STAGES.map((s, i) => (
            <article
              key={s.n}
              style={{
                display: 'grid',
                gridTemplateColumns: '38px 1fr',
                columnGap: 20,
                paddingBottom: 16,
              }}
            >
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'relative',
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #d946ef, #a855f7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    fontWeight: 800,
                    color: '#fff',
                    boxShadow: '0 6px 18px rgba(217, 70, 239, 0.28)',
                    zIndex: 1,
                  }}
                >
                  {s.n}
                </span>
                {i < STAGES.length - 1 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 18,
                      top: 40,
                      bottom: -2,
                      width: 2,
                      background:
                        'linear-gradient(rgba(217, 70, 239, 0.45), rgba(168, 85, 247, 0.12))',
                    }}
                  />
                )}
              </div>
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.025)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 18,
                  padding: '18px 20px 19px',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: 17,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    color: '#fff',
                  }}
                >
                  {s.title}
                </h2>
                <p
                  style={{
                    margin: '7px 0 0',
                    fontSize: 14.5,
                    lineHeight: 1.6,
                    color: 'rgba(232, 220, 250, 0.72)',
                  }}
                >
                  {s.body}
                </p>
              </div>
            </article>
          ))}
        </section>

        {/* things you don't have to do */}
        <section
          aria-labelledby="rooms-tellmore-freedoms-title"
          style={{
            marginTop: 30,
            padding: '22px 24px 8px',
            borderRadius: 20,
            background:
              'linear-gradient(180deg, rgba(217, 70, 239, 0.085), rgba(124, 58, 237, 0.03))',
            border: '1px solid rgba(217, 70, 239, 0.22)',
            ...revealStyle(0.18),
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'rgba(217, 70, 239, 0.16)',
                border: '1px solid rgba(217, 70, 239, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#f0abfc" strokeWidth="1.7" />
                <path d="M12 11v5" stroke="#f0abfc" strokeWidth="1.9" strokeLinecap="round" />
                <circle cx="12" cy="7.6" r="1.15" fill="#f0abfc" />
              </svg>
            </span>
            <span id="rooms-tellmore-freedoms-title">things you don&apos;t have to do</span>
          </div>
          <ul
            style={{
              listStyle: 'none',
              margin: '16px 0 0',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {FREEDOMS.map((f, i) => (
              <li
                key={f}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 13,
                  paddingBottom: 14,
                  borderBottom:
                    i < FREEDOMS.length - 1
                      ? '1px solid rgba(255, 255, 255, 0.055)'
                      : '0',
                  fontSize: 14.5,
                  lineHeight: 1.5,
                  color: 'rgba(245, 243, 255, 0.82)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    marginTop: 1,
                    borderRadius: '50%',
                    background: 'rgba(217, 70, 239, 0.14)',
                    border: '1px solid rgba(217, 70, 239, 0.34)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12.5l4.5 4.5L19 7"
                      stroke="#f0abfc"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {f}
              </li>
            ))}
          </ul>
        </section>

        {/* asymmetry callout */}
        <section
          style={{
            marginTop: 20,
            position: 'relative',
            padding: '20px 22px 20px 24px',
            borderRadius: 18,
            background: 'rgba(251, 191, 36, 0.045)',
            border: '1px solid rgba(251, 191, 36, 0.22)',
            overflow: 'hidden',
            ...revealStyle(0.24),
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              background: 'linear-gradient(#fcd34d, #f59e0b)',
            }}
          />
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#fcd34d',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#f59e0b',
              }}
            />
            one asymmetry
          </span>
          <p
            style={{
              margin: '11px 0 0',
              fontSize: 15,
              lineHeight: 1.58,
              color: 'rgba(248, 240, 224, 0.9)',
            }}
          >
            {asymmetryNote}
          </p>
        </section>

        {/* footer */}
        <div
          style={{
            marginTop: 44,
            textAlign: 'center',
            ...revealStyle(0.3),
          }}
        >
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 13,
              color: 'rgba(232, 220, 250, 0.4)',
              fontStyle: 'italic',
            }}
          >
            you haven&apos;t agreed to anything by reading this.
          </p>
          <button
            type="button"
            onClick={handleBack}
            style={{
              appearance: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 9,
              padding: '14px 26px',
              borderRadius: 999,
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.13)',
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 14.5,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M14.5 5l-7 7 7 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            back to the invitation
          </button>
        </div>
      </main>
    </div>
  );
};

export default RoomTellMore;
